/**
 * Check Out workflow — assign an asset to a user, location, or asset.
 * 1. Search/scan for the asset to check out
 * 2. Select checkout type (User / Location / Asset) and pick target
 * 3. Queue checkout operation
 */

import { router } from '../router.js';
import { enqueue } from '../db.js';
import { syncDirect } from '../sync.js';
import { searchAssets, getAssetByTag, getUsers, createUser, getLocations } from '../snipeit.js';
import { showToast } from '../toast.js';
import { hapticSuccess, hapticTap } from '../haptics.js';
import { startScanner, stopScanner } from './scanner.js';

export function renderCheckout() {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="page">
      <!-- Screen-reader live region for search result announcements -->
      <div id="co-status" aria-live="polite" aria-atomic="true" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;"></div>

      <div class="page-header">
        <button class="back-btn" id="back-btn" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1>Check Out</h1>
      </div>

      <!-- Step 1: Find asset -->
      <div class="card">
        <h2 style="margin-bottom:12px;">1. Find Asset</h2>

        <div style="display:flex; gap:8px; margin-bottom:12px;">
          <button class="btn btn-secondary btn-sm" id="co-scan-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>
            Scan
          </button>
        </div>

        <div id="co-scanner-area" class="hidden">
          <div class="camera-container" id="co-scanner-reader"></div>
          <button class="btn btn-secondary btn-block btn-sm" id="co-scanner-close">Close Scanner</button>
        </div>

        <div class="search-wrap">
          <span class="search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input type="text" id="co-asset-search" class="form-control" placeholder="Search by name, tag, or serial..." style="padding-left:38px;" aria-autocomplete="list" aria-controls="co-asset-results" />
        </div>

        <div id="co-asset-results" class="search-results hidden" role="listbox"></div>

        <div id="co-selected-asset" class="hidden" style="margin-top:8px;">
          <div class="card" style="background: var(--color-primary-light); border-color: var(--color-primary);">
            <strong id="co-asset-name"></strong>
            <div style="font-size:0.85rem; color:var(--color-text-muted);" id="co-asset-detail"></div>
          </div>
        </div>
      </div>

      <!-- Step 2: Assign To -->
      <div class="card">
        <h2 style="margin-bottom:12px;">2. Assign To</h2>

        <!-- Type selector -->
        <div id="co-type-selector" role="group" aria-label="Assign to" style="display:flex; gap:4px; margin-bottom:16px;">
          <button class="btn btn-primary btn-sm" data-type="user" id="co-type-user" aria-pressed="true">User</button>
          <button class="btn btn-secondary btn-sm" data-type="location" id="co-type-location" aria-pressed="false">Location</button>
          <button class="btn btn-secondary btn-sm" data-type="asset" id="co-type-asset" aria-pressed="false">Asset</button>
        </div>

        <!-- User panel -->
        <div id="co-panel-user">
          <div class="search-wrap">
            <span class="search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </span>
            <input type="text" id="co-user-search" class="form-control" placeholder="Search users..." style="padding-left:38px;" aria-autocomplete="list" aria-controls="co-user-results" />
          </div>
          <div id="co-user-results" class="search-results hidden" role="listbox"></div>
          <div id="co-selected-user" class="hidden" style="margin-top:8px;">
            <div class="card" style="background: var(--color-primary-light); border-color: var(--color-primary);">
              <strong id="co-user-name"></strong>
            </div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" id="co-new-user-btn" style="margin-top:8px;">+ Create User</button>
          <div id="co-new-user-form" class="hidden" style="margin-top:12px;">
            <div class="form-group">
              <label for="co-new-first">First Name</label>
              <input type="text" id="co-new-first" class="form-control" autocomplete="given-name" />
            </div>
            <div class="form-group">
              <label for="co-new-last">Last Name</label>
              <input type="text" id="co-new-last" class="form-control" autocomplete="family-name" />
            </div>
            <div class="form-group">
              <label for="co-new-username">Username</label>
              <input type="text" id="co-new-username" class="form-control" autocomplete="username" />
            </div>
            <div class="form-group">
              <label for="co-new-password">Password</label>
              <input type="password" id="co-new-password" class="form-control" autocomplete="new-password" />
            </div>
            <div style="display:flex; gap:8px;">
              <button type="button" class="btn btn-primary btn-sm" id="co-save-user">Save User</button>
              <button type="button" class="btn btn-secondary btn-sm" id="co-cancel-user">Cancel</button>
            </div>
          </div>
        </div>

        <!-- Location panel -->
        <div id="co-panel-location" class="hidden">
          <div class="search-wrap">
            <span class="search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </span>
            <input type="text" id="co-location-search" class="form-control" placeholder="Search locations..." style="padding-left:38px;" aria-autocomplete="list" aria-controls="co-location-results" />
          </div>
          <div id="co-location-results" class="search-results hidden" role="listbox"></div>
          <div id="co-selected-location" class="hidden" style="margin-top:8px;">
            <div class="card" style="background: var(--color-primary-light); border-color: var(--color-primary);">
              <strong id="co-location-name"></strong>
            </div>
          </div>
        </div>

        <!-- Target asset panel -->
        <div id="co-panel-asset" class="hidden">
          <div class="search-wrap">
            <span class="search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
            </span>
            <input type="text" id="co-target-asset-search" class="form-control" placeholder="Search target asset..." style="padding-left:38px;" aria-autocomplete="list" aria-controls="co-target-asset-results" />
          </div>
          <div id="co-target-asset-results" class="search-results hidden" role="listbox"></div>
          <div id="co-selected-target-asset" class="hidden" style="margin-top:8px;">
            <div class="card" style="background: var(--color-primary-light); border-color: var(--color-primary);">
              <strong id="co-target-asset-name"></strong>
              <div style="font-size:0.85rem; color:var(--color-text-muted);" id="co-target-asset-detail"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit -->
      <div style="display:flex; gap:8px; margin-top:16px;">
        <button class="btn btn-secondary" style="flex:1" id="co-queue" disabled>Add to Queue</button>
        <button class="btn btn-primary" style="flex:1" id="co-sync" disabled>Sync Now</button>
      </div>
    </div>
  `;

  let selectedAssetId = null;
  let selectedTargetId = null;
  let checkoutType = 'user';
  let searchTimeout = null;

  // ── Back ──
  document.getElementById('back-btn').addEventListener('click', () => {
    router.navigate('/');
  });

  // ── Type selector ──
  document.getElementById('co-type-selector').addEventListener('click', (e) => {
    const btn = e.target.closest('[data-type]');
    if (!btn) return;
    checkoutType = btn.dataset.type;
    selectedTargetId = null;

    document.querySelectorAll('#co-type-selector [data-type]').forEach((b) => {
      b.className = b === btn ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm';
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });

    document.getElementById('co-panel-user').classList.toggle('hidden', checkoutType !== 'user');
    document.getElementById('co-panel-location').classList.toggle('hidden', checkoutType !== 'location');
    document.getElementById('co-panel-asset').classList.toggle('hidden', checkoutType !== 'asset');

    updateSubmitState();
  });

  // ── Scan (source asset) ──
  document.getElementById('co-scan-btn').addEventListener('click', () => {
    hapticTap();
    const area = document.getElementById('co-scanner-area');
    area.classList.remove('hidden');
    startScanner('co-scanner-reader', async (text) => {
      hapticSuccess();
      stopScanner();
      area.classList.add('hidden');
      document.getElementById('co-asset-search').value = text;
      try {
        const asset = await getAssetByTag(text);
        selectAsset(asset);
      } catch {
        showToast('Asset not found for tag: ' + text);
      }
    });
  });

  document.getElementById('co-scanner-close').addEventListener('click', () => {
    stopScanner();
    document.getElementById('co-scanner-area').classList.add('hidden');
  });

  // ── Source asset search ──
  document.getElementById('co-asset-search').addEventListener('input', (e) => {
    clearTimeout(searchTimeout);
    const query = e.target.value.trim();
    if (query.length < 2) {
      document.getElementById('co-asset-results').classList.add('hidden');
      return;
    }
    searchTimeout = setTimeout(async () => {
      try {
        const res = await searchAssets(query);
        const container = document.getElementById('co-asset-results');
        const rows = res.rows || [];
        container.innerHTML = '';
        if (rows.length === 0) {
          container.innerHTML = `<div class="result-empty">No results for "${escapeHtml(query)}"</div>`;
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = 'No results found';
        } else {
          rows.forEach((a) => {
            const div = document.createElement('div');
            div.className = 'result-item';
            div.setAttribute('role', 'option');
            div.setAttribute('tabindex', '0');
            div.innerHTML = `
              <div>${escapeHtml(a.name || 'Unnamed')}</div>
              <div class="result-sub">Tag: ${escapeHtml(a.asset_tag || '')} | Serial: ${escapeHtml(a.serial || 'N/A')}</div>
            `;
            div.addEventListener('click', () => selectAsset(a));
            div.addEventListener('keydown', (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); selectAsset(a); }
            });
            container.appendChild(div);
          });
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = `${rows.length} result${rows.length !== 1 ? 's' : ''} found`;
        }
      } catch {
        showToast('Search failed');
      }
    }, 300);
  });

  function selectAsset(asset) {
    selectedAssetId = asset.id;
    document.getElementById('co-asset-name').textContent = asset.name || 'Unnamed';
    document.getElementById('co-asset-detail').textContent = `Tag: ${asset.asset_tag || 'N/A'} | Serial: ${asset.serial || 'N/A'}`;
    document.getElementById('co-selected-asset').classList.remove('hidden');
    document.getElementById('co-asset-results').classList.add('hidden');
    updateSubmitState();
  }

  // ── User search ──
  document.getElementById('co-user-search').addEventListener('input', (e) => {
    clearTimeout(searchTimeout);
    const query = e.target.value.trim();
    if (query.length < 2) {
      document.getElementById('co-user-results').classList.add('hidden');
      return;
    }
    searchTimeout = setTimeout(async () => {
      try {
        const res = await getUsers(query);
        const container = document.getElementById('co-user-results');
        const rows = res.rows || [];
        container.innerHTML = '';
        if (rows.length === 0) {
          container.innerHTML = `<div class="result-empty">No results for "${escapeHtml(query)}"</div>`;
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = 'No results found';
        } else {
          rows.forEach((u) => {
            const div = document.createElement('div');
            div.className = 'result-item';
            div.setAttribute('role', 'option');
            div.setAttribute('tabindex', '0');
            div.textContent = u.name || u.username;
            div.addEventListener('click', () => selectUser(u));
            div.addEventListener('keydown', (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); selectUser(u); }
            });
            container.appendChild(div);
          });
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = `${rows.length} result${rows.length !== 1 ? 's' : ''} found`;
        }
      } catch {
        showToast('User search failed');
      }
    }, 300);
  });

  function selectUser(user) {
    selectedTargetId = user.id;
    document.getElementById('co-user-name').textContent = user.name || user.username;
    document.getElementById('co-selected-user').classList.remove('hidden');
    document.getElementById('co-user-results').classList.add('hidden');
    updateSubmitState();
  }

  // ── Location search ──
  document.getElementById('co-location-search').addEventListener('input', (e) => {
    clearTimeout(searchTimeout);
    const query = e.target.value.trim();
    if (query.length < 2) {
      document.getElementById('co-location-results').classList.add('hidden');
      return;
    }
    searchTimeout = setTimeout(async () => {
      try {
        const res = await getLocations(query);
        const container = document.getElementById('co-location-results');
        const rows = res.rows || [];
        container.innerHTML = '';
        if (rows.length === 0) {
          container.innerHTML = `<div class="result-empty">No results for "${escapeHtml(query)}"</div>`;
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = 'No results found';
        } else {
          rows.forEach((l) => {
            const div = document.createElement('div');
            div.className = 'result-item';
            div.setAttribute('role', 'option');
            div.setAttribute('tabindex', '0');
            div.textContent = l.name;
            div.addEventListener('click', () => selectLocation(l));
            div.addEventListener('keydown', (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); selectLocation(l); }
            });
            container.appendChild(div);
          });
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = `${rows.length} result${rows.length !== 1 ? 's' : ''} found`;
        }
      } catch {
        showToast('Location search failed');
      }
    }, 300);
  });

  function selectLocation(location) {
    selectedTargetId = location.id;
    document.getElementById('co-location-name').textContent = location.name;
    document.getElementById('co-selected-location').classList.remove('hidden');
    document.getElementById('co-location-results').classList.add('hidden');
    updateSubmitState();
  }

  // ── Target asset search ──
  document.getElementById('co-target-asset-search').addEventListener('input', (e) => {
    clearTimeout(searchTimeout);
    const query = e.target.value.trim();
    if (query.length < 2) {
      document.getElementById('co-target-asset-results').classList.add('hidden');
      return;
    }
    searchTimeout = setTimeout(async () => {
      try {
        const res = await searchAssets(query);
        const container = document.getElementById('co-target-asset-results');
        const rows = (res.rows || []).filter((a) => a.id !== selectedAssetId);
        container.innerHTML = '';
        if (rows.length === 0) {
          container.innerHTML = `<div class="result-empty">No results for "${escapeHtml(query)}"</div>`;
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = 'No results found';
        } else {
          rows.forEach((a) => {
            const div = document.createElement('div');
            div.className = 'result-item';
            div.setAttribute('role', 'option');
            div.setAttribute('tabindex', '0');
            div.innerHTML = `
              <div>${escapeHtml(a.name || 'Unnamed')}</div>
              <div class="result-sub">Tag: ${escapeHtml(a.asset_tag || '')}</div>
            `;
            div.addEventListener('click', () => selectTargetAsset(a));
            div.addEventListener('keydown', (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); selectTargetAsset(a); }
            });
            container.appendChild(div);
          });
          container.classList.remove('hidden');
          document.getElementById('co-status').textContent = `${rows.length} result${rows.length !== 1 ? 's' : ''} found`;
        }
      } catch {
        showToast('Search failed');
      }
    }, 300);
  });

  function selectTargetAsset(asset) {
    selectedTargetId = asset.id;
    document.getElementById('co-target-asset-name').textContent = asset.name || 'Unnamed';
    document.getElementById('co-target-asset-detail').textContent = `Tag: ${asset.asset_tag || 'N/A'}`;
    document.getElementById('co-selected-target-asset').classList.remove('hidden');
    document.getElementById('co-target-asset-results').classList.add('hidden');
    updateSubmitState();
  }

  // ── Inline user creation ──
  document.getElementById('co-new-user-btn').addEventListener('click', () => {
    document.getElementById('co-new-user-form').classList.toggle('hidden');
  });

  document.getElementById('co-cancel-user').addEventListener('click', () => {
    document.getElementById('co-new-user-form').classList.add('hidden');
  });

  document.getElementById('co-save-user').addEventListener('click', async () => {
    const first = document.getElementById('co-new-first').value.trim();
    const last = document.getElementById('co-new-last').value.trim();
    const username = document.getElementById('co-new-username').value.trim();
    const password = document.getElementById('co-new-password').value;

    if (!first || !username || !password) {
      showToast('First name, username, and password are required');
      return;
    }

    try {
      const res = await createUser({
        first_name: first,
        last_name: last,
        username,
        password,
        password_confirmation: password,
      });
      const user = res.payload || res;
      selectUser({ id: user.id, name: `${first} ${last}`.trim(), username });
      document.getElementById('co-new-user-form').classList.add('hidden');
      showToast('User created');
    } catch (err) {
      showToast('Failed: ' + err.message);
    }
  });

  // ── Submit ──
  function updateSubmitState() {
    const ready = !!(selectedAssetId && selectedTargetId);
    document.getElementById('co-queue').disabled = !ready;
    document.getElementById('co-sync').disabled = !ready;
  }

  function buildCheckoutPayload() {
    const payload = {
      asset_id: selectedAssetId,
      checkout_to_type: checkoutType,
      // Display names for queue summary — not sent to Snipe-IT API
      asset_name: document.getElementById('co-asset-name').textContent,
    };
    if (checkoutType === 'user') {
      payload.assigned_user = selectedTargetId;
      payload.target_name = document.getElementById('co-user-name').textContent;
    } else if (checkoutType === 'location') {
      payload.assigned_location = selectedTargetId;
      payload.target_name = document.getElementById('co-location-name').textContent;
    } else if (checkoutType === 'asset') {
      payload.assigned_asset = selectedTargetId;
      payload.target_name = document.getElementById('co-target-asset-name').textContent;
    }
    return payload;
  }

  document.getElementById('co-queue').addEventListener('click', async () => {
    if (!selectedAssetId || !selectedTargetId) return;
    await enqueue('checkout', buildCheckoutPayload());
    hapticSuccess();
    showToast('Check out added to queue');
    window.dispatchEvent(new CustomEvent('queue-updated'));
    router.navigate('/');
  });

  document.getElementById('co-sync').addEventListener('click', async () => {
    if (!selectedAssetId || !selectedTargetId) return;
    const btn = document.getElementById('co-sync');
    btn.disabled = true;
    btn.textContent = 'Syncing...';
    try {
      await syncDirect('checkout', buildCheckoutPayload());
      hapticSuccess();
      showToast('Checked out');
      router.navigate('/');
    } catch (err) {
      showToast('Sync failed: ' + err.message);
      btn.disabled = false;
      btn.textContent = 'Sync Now';
    }
  });

  return () => {
    stopScanner();
  };
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
